import { skyBossDesign } from '../../../packages/domain/src/85-sky-boss-designs.mjs';

/** Add bounded, jointed silhouette accents to an existing authored guardian.
 * Each hinge is an ordinary presentation part captured by model113. Geometry
 * and source materials are shared; guardian118 clones encounter materials and
 * disposes those clones. No accessory is a new collision target. */
export function ornamentSkyBoss(T, group, models, resources) {
  const design = skyBossDesign(group.userData.design);
  if (!design || group.userData.design === 0 || group.userData.designOrnamented) return;
  group.userData.designOrnamented = true;
  const cache = models.sentinel;
  if (!cache.designShapes) {
    const petal = new T.SphereGeometry(1, 12, 8);
    const prism = new T.OctahedronGeometry(1, 0);
    const blade = new T.ConeGeometry(0.3, 1.7, 5);
    const gear = new T.TorusGeometry(0.5, 0.1, 6, 12);
    cache.designShapes = { petal, prism, blade, gear };
    for (const shape of Object.values(cache.designShapes)) resources.add(shape);
    cache.designMaterials = new Map();
  }
  let material = cache.designMaterials.get(design.trim);
  if (!material) {
    material = new T.MeshStandardMaterial({
      color: design.trim,
      metalness: ['gear', 'blade', 'crown', 'solar'].includes(design.motif) ? 0.65 : 0.24,
      roughness: design.motif === 'prism' ? 0.22 : 0.48,
    });
    cache.designMaterials.set(design.trim, material);
    resources.add(material);
  }
  const root = group.children[0];
  if (!root) return;
  const head = root.getObjectByName('head') ?? root.getObjectByName('torso') ?? root;
  const motif = design.motif;
  const round = ['petal', 'lantern', 'tidal'].includes(motif);
  const radial = ['petal', 'prism', 'gear', 'solar'].includes(motif);
  const count = radial ? 8 : 6;
  for (let index = 0; index < count; index++) {
    const side = index % 2 ? 1 : -1;
    const hinge = new T.Group();
    // Preserve familiar authored articulation names so all poses reset to bind.
    hinge.name = `shell_design_${side < 0 ? 'left' : 'right'}_${index}`;
    const angle = (index / count) * Math.PI * 2;
    const layer = Math.floor(index / 2);
    hinge.position.set(
      radial ? Math.cos(angle) * 1.1 : side * (0.85 + layer * 0.14),
      radial ? Math.sin(angle) * 1.1 : 0.55 - layer * 0.3,
      -0.65 - (radial ? 0 : layer * 0.45),
    );
    hinge.rotation.z = radial ? angle - Math.PI / 2 : -side * (0.3 + layer * 0.24);
    const shape =
      cache.designShapes[
        motif === 'gear'
          ? 'gear'
          : round
            ? 'petal'
            : motif === 'prism' || motif === 'stone'
              ? 'prism'
              : 'blade'
      ];
    const mesh = new T.Mesh(shape, material);
    mesh.name = `articulated_${motif}_fin`;
    mesh.position.y = 0.45;
    mesh.scale.set(round ? 0.32 : 0.5, motif === 'lantern' ? 0.55 : 0.9, round ? 0.13 : 0.36);
    hinge.add(mesh);
    head.add(hinge);
  }
}
